import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ScrollArea } from '../components/ui/scroll-area';
import { MasterOrdersTab } from '../components/MasterOrdersTab';
import {
  WooOrderDetailDialog,
  extractChildrenFromOrder,
} from '../components/WooOrderDetailDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Send, 
  Sparkles,
  RefreshCw,
  Trash2,
  Database,
  MessageSquare,
  ShoppingCart,
  Globe,
  Key,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  Package,
  Search,
  TrendingUp,
  Users,
  Calendar,
  Download
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { cn, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const CheckOrdersPage = () => {
  const { user } = useAuth();
  const { isBarbiePink } = useTheme();
  const isAdmin = user?.role === 'admin';
  
  // Google Sheet Config state
  const [sheetConfig, setSheetConfig] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('Orders Sheet');
  const [sheetSaving, setSheetSaving] = useState(false);
  const [sheetTesting, setSheetTesting] = useState(false);
  const [sheetTestResult, setSheetTestResult] = useState(null);
  
  // WooCommerce Config state
  const [wooConfig, setWooConfig] = useState(null);
  const [wooSiteUrl, setWooSiteUrl] = useState('');
  const [wooConsumerKey, setWooConsumerKey] = useState('');
  const [wooConsumerSecret, setWooConsumerSecret] = useState('');
  const [wooEnabled, setWooEnabled] = useState(true);
  const [wooSaving, setWooSaving] = useState(false);
  const [wooTesting, setWooTesting] = useState(false);
  const [wooTestResult, setWooTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  
  const [configLoading, setConfigLoading] = useState(true);
  
  // AI Query state
  const [query, setQuery] = useState('');
  const [querying, setQuerying] = useState(false);
  const [conversation, setConversation] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Main tab state
  // Operator request (Mar 2026): Check is the default landing tab for
  // every user — admins AND normal users — so the AI Order Search panel
  // is the first thing they see.
  const [activeMainTab, setActiveMainTab] = useState('check');
  // Operator request (Feb 28 2026): hide the Data Source + Configuration
  // panels on the Check tab by default — they're rarely changed and were
  // dominating the screen. Expand on demand via the "Show advanced" toggle.
  // (Admins-only — the toggle button doesn't render for normal users since
  //  Data Source / Configuration panels are admin-only anyway.)
  const [showCheckAdvanced, setShowCheckAdvanced] = useState(false);
  const [selectedRecentOrder, setSelectedRecentOrder] = useState(null);
  
  // Recent Orders state
  const [recentOrdersSummary, setRecentOrdersSummary] = useState(null);
  const [recentOrdersList, setRecentOrdersList] = useState([]);
  const [recentOrdersTotal, setRecentOrdersTotal] = useState(0);
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [ordersListOpen, setOrdersListOpen] = useState(true);
  const [recentOrdersSearch, setRecentOrdersSearch] = useState('');
  const [recentOrdersStatusFilter, setRecentOrdersStatusFilter] = useState('');
  // Date-range filter — defaults to Jan 1 of current Sydney year → today.
  const _sydYear = (() => {
    try {
      return parseInt(new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney', year: 'numeric',
      }).format(new Date()), 10);
    } catch { return new Date().getFullYear(); }
  })();
  const _sydToday = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date());
      return parts; // YYYY-MM-DD
    } catch { return new Date().toISOString().slice(0, 10); }
  })();
  const [ordersFromDate, setOrdersFromDate] = useState(`${_sydYear}-01-01`);
  const [ordersToDate, setOrdersToDate] = useState(_sydToday);
  const [exportingOrdersCsv, setExportingOrdersCsv] = useState(false);
  
  // AI Query for Recent Orders
  const [recentAIQuery, setRecentAIQuery] = useState('');
  const [recentAIQuerying, setRecentAIQuerying] = useState(false);
  const [recentAIConversation, setRecentAIConversation] = useState([]);
  const recentAIMessagesEndRef = useRef(null);

  useEffect(() => {
    fetchAllConfig();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);
  
  useEffect(() => {
    recentAIMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [recentAIConversation]);
  
  // Load orders data when switching to that tab OR when filters change.
  // Search is debounced 350ms so typing doesn't hammer the backend.
  useEffect(() => {
    if (activeMainTab !== 'recent') return;
    const t = setTimeout(() => { fetchRecentOrdersData(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMainTab, recentOrdersSearch, recentOrdersStatusFilter, ordersFromDate, ordersToDate]);

  const fetchRecentOrdersData = async () => {
    setRecentOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (ordersFromDate) params.set('from_date', ordersFromDate);
      if (ordersToDate) params.set('to_date', ordersToDate);
      if (recentOrdersStatusFilter) params.set('status', recentOrdersStatusFilter);
      if (recentOrdersSearch.trim()) params.set('search', recentOrdersSearch.trim());
      params.set('limit', '20');

      const [summaryResult, listResult, syncResult] = await Promise.allSettled([
        authFetch(`${API}/check-orders/recent-orders/summary?days=7`),
        authFetch(`${API}/check-orders/recent-orders/list?${params.toString()}`),
        authFetch(`${API}/check-orders/recent-orders/sync-status`),
      ]);

      let allFailed = true;

      if (summaryResult.status === 'fulfilled' && summaryResult.value.ok) {
        try { setRecentOrdersSummary(await summaryResult.value.json()); allFailed = false; } catch (_e) { /* noop */ }
      }
      if (listResult.status === 'fulfilled' && listResult.value.ok) {
        try {
          const data = await listResult.value.json();
          setRecentOrdersList(data.orders || []);
          setRecentOrdersTotal(data.total || (data.orders || []).length);
          allFailed = false;
        } catch (_e) { /* noop */ }
      }
      if (syncResult.status === 'fulfilled' && syncResult.value.ok) {
        try { setSyncStatus(await syncResult.value.json()); allFailed = false; } catch (_e) { /* noop */ }
      }

      if (allFailed) {
        console.error('All orders fetches failed', { summaryResult, listResult, syncResult });
        toast.error('Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Failed to load orders');
    } finally {
      setRecentOrdersLoading(false);
    }
  };

  const handleExportOrdersCsv = async () => {
    setExportingOrdersCsv(true);
    try {
      const params = new URLSearchParams();
      if (ordersFromDate) params.set('from_date', ordersFromDate);
      if (ordersToDate) params.set('to_date', ordersToDate);
      if (recentOrdersStatusFilter) params.set('status', recentOrdersStatusFilter);
      if (recentOrdersSearch.trim()) params.set('search', recentOrdersSearch.trim());

      const res = await authFetch(`${API}/check-orders/recent-orders/export.csv?${params.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.slice(0, 200) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${ordersFromDate}_to_${ordersToDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${recentOrdersTotal || recentOrdersList.length} orders`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExportingOrdersCsv(false);
    }
  };

  const handleSyncOrders = async () => {
    setSyncing(true);
    try {
      const response = await authJsonFetch(`${API}/check-orders/recent-orders/sync`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Sync complete: ${result.new} new, ${result.updated} updated`);
        // Refresh data
        await fetchRecentOrdersData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Failed to sync orders');
    } finally {
      setSyncing(false);
    }
  };

  const handleRecentAIQuery = async (e) => {
    e.preventDefault();
    if (!recentAIQuery.trim()) return;
    
    const userQuery = recentAIQuery;
    setRecentAIQuery('');
    setRecentAIConversation(prev => [...prev, { role: 'user', content: userQuery }]);
    setRecentAIQuerying(true);
    
    try {
      const response = await authJsonFetch(`${API}/check-orders/recent-orders/ai-query`, {
        method: 'POST',
        body: JSON.stringify({ query: userQuery })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentAIConversation(prev => [...prev, { 
          role: 'assistant', 
          content: data.response,
          orders_analyzed: data.orders_analyzed,
          orders_matched: data.orders_matched,
          source: data.source
        }]);
      } else {
        const err = await response.json();
        setRecentAIConversation(prev => [...prev, { 
          role: 'assistant', 
          content: `Error: ${err.detail || 'Query failed'}`,
          error: true
        }]);
      }
    } catch (err) {
      console.error('AI query error:', err);
      setRecentAIConversation(prev => [...prev, { 
        role: 'assistant', 
        content: 'Failed to process query. Please try again.',
        error: true
      }]);
    } finally {
      setRecentAIQuerying(false);
    }
  };

  // Server-side filtering — keep alias name for existing JSX usages.
  const filteredRecentOrders = recentOrdersList;

  const fetchAllConfig = async () => {
    try {
      const [sheetRes, wooRes] = await Promise.all([
        authFetch(`${API}/check-orders/orders-sheet/config`),
        authFetch(`${API}/check-orders/woocommerce/config`)
      ]);
      
      // Handle sheet config (may fail if not configured)
      if (sheetRes.ok) {
        const sheetData = await sheetRes.json();
        setSheetConfig(sheetData);
        if (sheetData.sheet_url) {
          setSheetUrl(sheetData.sheet_url);
          setSheetName(sheetData.sheet_name || 'Orders Sheet');
        }
      } else {
        setSheetConfig({ connected: false });
      }
      
      // Handle WooCommerce config
      if (wooRes.ok) {
        const wooData = await wooRes.json();
        setWooConfig(wooData);
        if (wooData.site_url) {
          setWooSiteUrl(wooData.site_url);
          setWooConsumerKey(wooData.consumer_key || '');
          setWooEnabled(wooData.enabled !== false);
        }
      } else {
        setWooConfig({ connected: false, enabled: false });
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      // Set defaults on error
      setSheetConfig({ connected: false });
      setWooConfig({ connected: false, enabled: false });
    } finally {
      setConfigLoading(false);
    }
  };

  // Google Sheet functions
  const saveSheetConfig = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Please enter a Google Sheet URL');
      return;
    }
    
    setSheetSaving(true);
    try {
      const res = await authJsonFetch(`${API}/check-orders/orders-sheet/config`, {
        method: 'POST',
        body: JSON.stringify({ spreadsheet_id: sheetUrl, sheet_name: sheetName })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save configuration');
      }
      
      toast.success('Google Sheet configuration saved');
      fetchAllConfig();
      setSheetTestResult(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSheetSaving(false);
    }
  };

  const testSheetConnection = async () => {
    setSheetTesting(true);
    setSheetTestResult(null);
    try {
      const res = await authFetch(`${API}/check-orders/orders-sheet/test`, {
        method: 'POST'
      });
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Connection test failed');
      }
      
      setSheetTestResult(data);
      toast.success('Successfully connected to Google Sheet');
    } catch (err) {
      setSheetTestResult({ success: false, message: err.message });
      toast.error(err.message);
    } finally {
      setSheetTesting(false);
    }
  };

  const removeSheetConfig = async () => {
    if (!window.confirm('Are you sure you want to remove the Google Sheet configuration?')) {
      return;
    }
    
    try {
      const res = await authFetch(`${API}/check-orders/orders-sheet/config`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to remove configuration');
      }
      
      toast.success('Google Sheet configuration removed');
      setSheetConfig({ connected: false });
      setSheetUrl('');
      setSheetTestResult(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // WooCommerce functions
  const saveWooConfig = async () => {
    if (!wooSiteUrl.trim() || !wooConsumerKey.trim()) {
      toast.error('Site URL and Consumer Key are required');
      return;
    }
    
    setWooSaving(true);
    try {
      const res = await authJsonFetch(`${API}/check-orders/woocommerce/config`, {
        method: 'POST',
        body: JSON.stringify({
          site_url: wooSiteUrl,
          consumer_key: wooConsumerKey,
          consumer_secret: wooConsumerSecret,
          enabled: wooEnabled
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save configuration');
      }
      
      toast.success('WooCommerce configuration saved');
      fetchAllConfig();
      setWooTestResult(null);
      setWooConsumerSecret(''); // Clear secret after save
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWooSaving(false);
    }
  };

  const toggleWooEnabled = async () => {
    const newEnabled = !wooEnabled;
    setWooEnabled(newEnabled);
    
    try {
      const res = await authJsonFetch(`${API}/check-orders/woocommerce/toggle`, {
        method: 'POST',
        body: JSON.stringify({ enabled: newEnabled })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to toggle');
      }
      
      toast.success(`WooCommerce ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setWooEnabled(!newEnabled); // Revert
      toast.error(err.message);
    }
  };

  const testWooConnection = async () => {
    setWooTesting(true);
    setWooTestResult(null);
    try {
      const res = await authFetch(`${API}/check-orders/woocommerce/test`, {
        method: 'POST'
      });
      
      const data = await res.json();
      setWooTestResult(data);
      
      if (data.success) {
        toast.success('Successfully connected to WooCommerce');
      } else {
        toast.error(data.message || 'Connection test failed');
      }
    } catch (err) {
      setWooTestResult({ success: false, message: err.message });
      toast.error(err.message);
    } finally {
      setWooTesting(false);
    }
  };

  // AI Query functions
  const submitQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    const userQuery = query.trim();
    setQuery('');
    setQuerying(true);
    
    setConversation(prev => [...prev, { role: 'user', content: userQuery }]);
    
    try {
      const res = await authJsonFetch(`${API}/check-orders/query`, {
        method: 'POST',
        body: JSON.stringify({ query: userQuery })
      });
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Invalid response from server');
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Query failed');
      }
      
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        dataSource: data.data_source,
        rowsAnalyzed: data.data_rows_analyzed || data.orders_found
      }]);
    } catch (err) {
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${err.message}`,
        isError: true
      }]);
      toast.error(err.message);
    } finally {
      setQuerying(false);
    }
  };

  const clearConversation = () => {
    setConversation([]);
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const hasAnyConnection = (wooConfig?.connected && wooConfig?.enabled) || sheetConfig?.connected;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ShoppingCart className="w-8 h-8 text-primary" />
          Check Orders
        </h1>
        <p className="text-muted-foreground mt-1">
          Query order data using AI (WooCommerce + Google Sheets)
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
        <TabsList className={`grid w-full ${isAdmin ? 'max-w-2xl grid-cols-3' : 'max-w-md grid-cols-2'}`}>
          {isAdmin && (
            <TabsTrigger value="master" data-testid="master-orders-tab-trigger">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Master Orders
            </TabsTrigger>
          )}
          {/* Check tab is now available to ALL users (was admin-only).
              For normal users the "advanced" Data Source / Configuration
              panels stay hidden — they only see the AI Order Search. */}
          <TabsTrigger value="check" data-testid="check-tab">
            <Search className="w-4 h-4 mr-2" />
            Check
          </TabsTrigger>
          <TabsTrigger value="recent" data-testid="recent-orders-tab">
            <Clock className="w-4 h-4 mr-2" />
            Orders
          </TabsTrigger>
        </TabsList>

        {/* Master Orders Tab — admin only, default for admins */}
        {isAdmin && (
          <TabsContent value="master" className="mt-6">
            <MasterOrdersTab />
          </TabsContent>
        )}

        {/* Check Tab — available to all users; advanced panels admin-only */}
        <TabsContent value="check" className="mt-6">
          {/* "Show advanced" toggle is admin-only — normal users don't
              have access to Data Source / Configuration anyway. */}
          {isAdmin && (
            <div className="flex items-center justify-end mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCheckAdvanced(v => !v)}
                data-testid="check-toggle-advanced"
                className="text-xs"
              >
                {showCheckAdvanced ? 'Hide' : 'Show'} advanced (Data Source / Configuration)
              </Button>
            </div>
          )}
          <div className={cn(
            "grid grid-cols-1 gap-6",
            (isAdmin && showCheckAdvanced) ? "lg:grid-cols-3" : "lg:grid-cols-1"
          )}>
            {isAdmin && showCheckAdvanced && (
            /* Left Column - Configuration (admin + advanced toggle on) */
            <div className="space-y-6">
              {/* Data Source Status */}
              <Card className={cn(isBarbiePink && "card-brutalist")}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Data Sources</CardTitle>
                  <CardDescription>
                    WooCommerce is prioritized. Google Sheet is fallback.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* WooCommerce Status */}
                  <div className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border-2",
                    wooConfig?.connected && wooConfig?.enabled
                      ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900"
                      : "bg-slate-50 border-slate-200 dark:bg-slate-900/50 dark:border-slate-700"
                  )}>
                    <ShoppingCart className={cn(
                      "w-5 h-5",
                      wooConfig?.connected && wooConfig?.enabled ? "text-green-600" : "text-slate-400"
                    )} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">WooCommerce API</p>
                      <p className="text-xs text-muted-foreground">
                        {wooConfig?.connected 
                          ? (wooConfig?.enabled ? 'Primary Source' : 'Disabled')
                          : 'Not configured'
                        }
                      </p>
                    </div>
                    {wooConfig?.connected && wooConfig?.enabled && (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    )}
                  </div>
                  
                  {/* Google Sheet Status */}
                  <div className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border-2",
                    sheetConfig?.connected && (!wooConfig?.connected || !wooConfig?.enabled)
                      ? "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900"
                      : "bg-slate-50 border-slate-200 dark:bg-slate-900/50 dark:border-slate-700"
                  )}>
                    <FileSpreadsheet className={cn(
                      "w-5 h-5",
                      sheetConfig?.connected ? "text-blue-600" : "text-slate-400"
                    )} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Google Sheet</p>
                      <p className="text-xs text-muted-foreground">
                        {sheetConfig?.connected ? 'Fallback Source' : 'Not configured'}
                      </p>
                    </div>
                    {sheetConfig?.connected && (!wooConfig?.connected || !wooConfig?.enabled) && (
                      <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

          {/* Admin Configuration */}
          {isAdmin && (
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="woocommerce" className="w-full">
                  <TabsList className="w-full grid grid-cols-2">
                    <TabsTrigger value="woocommerce" className="text-xs">
                      <ShoppingCart className="w-3 h-3 mr-1" />
                      WooCommerce
                    </TabsTrigger>
                    <TabsTrigger value="sheet" className="text-xs">
                      <FileSpreadsheet className="w-3 h-3 mr-1" />
                      Google Sheet
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* WooCommerce Tab */}
                  <TabsContent value="woocommerce" className="space-y-4 mt-4">
                    {wooConfig?.connected && (
                      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="woo-toggle" className="text-sm">Enable WooCommerce</Label>
                        </div>
                        <Switch
                          id="woo-toggle"
                          checked={wooEnabled}
                          onCheckedChange={toggleWooEnabled}
                          data-testid="woo-toggle"
                        />
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Site URL</Label>
                        <Input
                          placeholder="https://yourstore.com"
                          value={wooSiteUrl}
                          onChange={(e) => setWooSiteUrl(e.target.value)}
                          data-testid="woo-site-url"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Consumer Key</Label>
                        <Input
                          placeholder="ck_xxxxxxxxxxxxx"
                          value={wooConsumerKey}
                          onChange={(e) => setWooConsumerKey(e.target.value)}
                          data-testid="woo-consumer-key"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Consumer Secret</Label>
                        <div className="relative">
                          <Input
                            type={showSecret ? "text" : "password"}
                            placeholder={wooConfig?.consumer_secret_masked || "cs_xxxxxxxxxxxxx"}
                            value={wooConsumerSecret}
                            onChange={(e) => setWooConsumerSecret(e.target.value)}
                            data-testid="woo-consumer-secret"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                            onClick={() => setShowSecret(!showSecret)}
                          >
                            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave empty to keep existing secret
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={saveWooConfig}
                        disabled={wooSaving}
                        className="flex-1"
                        data-testid="save-woo-btn"
                      >
                        {wooSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Save
                      </Button>
                      {wooConfig?.connected && (
                        <Button
                          onClick={testWooConnection}
                          disabled={wooTesting}
                          variant="outline"
                          data-testid="test-woo-btn"
                        >
                          {wooTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                    
                    {wooTestResult && (
                      <div className={cn(
                        "p-3 rounded-lg text-sm",
                        wooTestResult.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                      )}>
                        {wooTestResult.success ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {wooTestResult.message}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            {wooTestResult.message}
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Google Sheet Tab */}
                  <TabsContent value="sheet" className="space-y-4 mt-4">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Sheet URL</Label>
                        <Input
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                          value={sheetUrl}
                          onChange={(e) => setSheetUrl(e.target.value)}
                          data-testid="sheet-url"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Sheet Name (Display)</Label>
                        <Input
                          placeholder="Orders Sheet"
                          value={sheetName}
                          onChange={(e) => setSheetName(e.target.value)}
                          data-testid="sheet-name"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={saveSheetConfig}
                        disabled={sheetSaving}
                        className="flex-1"
                        data-testid="save-sheet-btn"
                      >
                        {sheetSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Save
                      </Button>
                      {sheetConfig?.connected && (
                        <>
                          <Button
                            onClick={testSheetConnection}
                            disabled={sheetTesting}
                            variant="outline"
                            data-testid="test-sheet-btn"
                          >
                            {sheetTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </Button>
                          <Button
                            onClick={removeSheetConfig}
                            variant="outline"
                            className="text-destructive hover:bg-destructive hover:text-white"
                            data-testid="remove-sheet-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                    
                    {sheetTestResult && (
                      <div className={cn(
                        "p-3 rounded-lg text-sm",
                        sheetTestResult.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                      )}>
                        {sheetTestResult.success ? (
                          <>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              {sheetTestResult.message}
                            </div>
                            <p className="mt-1 text-xs">
                              Columns: {sheetTestResult.headers?.join(', ')}
                            </p>
                            <p className="text-xs">Rows: {sheetTestResult.row_count}</p>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            {sheetTestResult.message}
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
        )}

        {/* Right Column - AI Chat */}
        <div className={(isAdmin && showCheckAdvanced) ? "lg:col-span-2" : ""}>
          <Card className={cn("h-[600px] flex flex-col", isBarbiePink && "card-brutalist")}>
            <CardHeader className="pb-3 flex-shrink-0">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                AI Order Search
              </CardTitle>
              <CardDescription>
                Ask questions about your orders using natural language
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto mb-4 space-y-4 pr-2">
                {conversation.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-8">
                    <MessageSquare className="w-12 h-12 mb-4 opacity-30" />
                    <p className="font-medium">Ask about your orders</p>
                    <p className="text-sm mt-2">Try queries like:</p>
                    <div className="mt-3 space-y-2 text-sm">
                      <p className="bg-muted px-3 py-1 rounded-full">"132093"</p>
                      <p className="bg-muted px-3 py-1 rounded-full">"Tom Howlan"</p>
                      <p className="bg-muted px-3 py-1 rounded-full">"Orders from last week"</p>
                    </div>
                  </div>
                ) : (
                  conversation.map((msg, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex",
                        msg.role === 'user' ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-lg p-4",
                          msg.role === 'user'
                            ? "bg-primary text-primary-foreground"
                            : msg.isError
                            ? "bg-destructive/10 text-destructive border border-destructive/20"
                            : "bg-muted"
                        )}
                      >
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        {msg.role === 'assistant' && !msg.isError && (
                          <div className="mt-2 pt-2 border-t border-current/10 flex items-center gap-2 text-xs opacity-60">
                            {msg.dataSource?.toLowerCase() === 'woocommerce' ? (
                              <><ShoppingCart className="w-3 h-3" /> WooCommerce</>
                            ) : (
                              <><FileSpreadsheet className="w-3 h-3" /> Google Sheet</>
                            )}
                            {msg.rowsAnalyzed && <span>• {msg.rowsAnalyzed} orders</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <form onSubmit={submitQuery} className="flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={hasAnyConnection ? "Search orders by number, name, or ask a question..." : "Configure a data source first..."}
                    disabled={querying || !hasAnyConnection}
                    className="flex-1"
                    data-testid="query-input"
                  />
                  <Button
                    type="submit"
                    disabled={querying || !query.trim() || !hasAnyConnection}
                    data-testid="query-submit"
                  >
                    {querying ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                  {conversation.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearConversation}
                      data-testid="clear-chat"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        {/* Recent Orders Tab */}
        <TabsContent value="recent" className="mt-6 space-y-6">

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Orders (7 days)</p>
                    <p className="text-3xl font-bold">
                      {recentOrdersLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : recentOrdersSummary?.total_orders || 0}
                    </p>
                  </div>
                  <Package className="w-10 h-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            
            {/* Total Revenue + Completed cards intentionally removed per user request — Recent (legacy) tab keeps the count summary minimal. */}
            
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Processing</p>
                    <p className="text-3xl font-bold">
                      {recentOrdersLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : recentOrdersSummary?.by_status?.processing || 0}
                    </p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-blue-500/20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sync Status & Actions */}
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <RefreshCw className="w-5 h-5" />
                    Sync Status
                  </CardTitle>
                  <CardDescription>
                    Orders sync automatically once a day at 1am Sydney time
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleSyncOrders} 
                  disabled={syncing || !isAdmin}
                  data-testid="sync-orders-btn"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Now
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Last sync: {syncStatus?.last_sync ? formatDateTime(syncStatus.last_sync) : 'Never'}</span>
                </div>
                {syncStatus?.last_stats && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {syncStatus.last_stats.new} new, {syncStatus.last_stats.updated} updated
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Orders List - Collapsible */}
          <Collapsible open={ordersListOpen} onOpenChange={setOrdersListOpen}>
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {ordersListOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      <CardTitle className="text-lg">Orders</CardTitle>
                      <Badge variant="secondary" data-testid="orders-total-badge">
                        {recentOrdersTotal} match{recentOrdersTotal === 1 ? '' : 'es'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  {/* Date range + Export */}
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[140px]">
                      <Label className="text-xs">From</Label>
                      <Input
                        type="date"
                        value={ordersFromDate}
                        onChange={(e) => setOrdersFromDate(e.target.value)}
                        data-testid="orders-from-date"
                      />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <Label className="text-xs">To</Label>
                      <Input
                        type="date"
                        value={ordersToDate}
                        onChange={(e) => setOrdersToDate(e.target.value)}
                        data-testid="orders-to-date"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportOrdersCsv}
                      disabled={exportingOrdersCsv || recentOrdersList.length === 0}
                      data-testid="orders-export-csv-btn"
                    >
                      {exportingOrdersCsv
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <Download className="w-4 h-4 mr-1" />}
                      Export CSV
                    </Button>
                  </div>

                  {/* Search and Status Filter */}
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={recentOrdersSearch}
                        onChange={(e) => setRecentOrdersSearch(e.target.value)}
                        placeholder="Search — multiple keywords AND together (e.g. Blue +Family +Toby). Searches order #, customer, item description, child name & class."
                        className="pl-9"
                        data-testid="recent-orders-search"
                      />
                    </div>
                    <select
                      value={recentOrdersStatusFilter}
                      onChange={(e) => setRecentOrdersStatusFilter(e.target.value)}
                      className="px-3 py-2 border rounded-md bg-background text-sm"
                      data-testid="recent-orders-status-filter"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="on-hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  
                  {/* Orders Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-24">Order #</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Item Ordered</TableHead>
                          <TableHead className="w-28">Status</TableHead>
                          <TableHead className="w-24 text-right">Total</TableHead>
                          <TableHead className="w-32">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recentOrdersLoading ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8">
                              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                              <p className="text-sm text-muted-foreground mt-2">Loading orders...</p>
                            </TableCell>
                          </TableRow>
                        ) : filteredRecentOrders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              {recentOrdersTotal === 0
                                ? "No orders match your filters. Try widening the date range or clearing the search."
                                : "No orders match your search criteria."
                              }
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRecentOrders.map((order) => {
                            const orderChildren = extractChildrenFromOrder(order);
                            const itemNames = (order.line_items || []).map((it) => it.name).filter(Boolean);
                            const itemSummary = itemNames.join(', ');
                            return (
                            <TableRow
                              key={order.woo_order_id}
                              className="cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => setSelectedRecentOrder(order)}
                              data-testid={`recent-order-row-${order.woo_order_id}`}
                            >
                              <TableCell className="font-medium">#{order.order_number}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="text-sm">
                                    {order.billing?.first_name} {order.billing?.last_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{order.billing?.email}</p>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm align-top">
                                <div className="font-medium leading-snug">
                                  {itemSummary
                                    ? itemSummary.length > 80 ? itemSummary.slice(0, 80) + '…' : itemSummary
                                    : '—'}
                                </div>
                                {orderChildren.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {orderChildren.map((c, i) => (
                                      <Badge
                                        key={`${order.woo_order_id}-c-${i}`}
                                        variant="outline"
                                        className="text-[10px] font-normal"
                                        data-testid={`recent-child-${order.woo_order_id}-${i}`}
                                      >
                                        {c.name}{c.class_name ? ` · ${c.class_name}` : ''}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={
                                  order.status === 'completed' ? 'default' :
                                  order.status === 'processing' ? 'secondary' :
                                  order.status === 'cancelled' || order.status === 'failed' ? 'destructive' :
                                  'outline'
                                } className="text-xs capitalize">
                                  {order.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                ${parseFloat(order.total || 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {order.date_created?.slice(0, 10)}
                              </TableCell>
                            </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {recentOrdersTotal > filteredRecentOrders.length && (
                    <p className="text-sm text-muted-foreground text-center">
                      Showing {filteredRecentOrders.length} of {recentOrdersTotal} orders. Refine your search to narrow results.
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </TabsContent>
      </Tabs>

      <WooOrderDetailDialog
        open={!!selectedRecentOrder}
        onOpenChange={(o) => { if (!o) setSelectedRecentOrder(null); }}
        order={selectedRecentOrder}
        isAdmin={isAdmin}
        onRefunded={() => {
          // refresh the list so the order's status badge reflects the new refund
          if (typeof fetchRecentOrdersData === 'function') fetchRecentOrdersData();
        }}
      />
    </div>
  );
};

export default CheckOrdersPage;
